import { useEffect, useMemo } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useMutation, useQuery } from "@tanstack/react-query";

import { TLeadFormData } from "../Lead.model";
import { requests } from "../../../../services/http-common";
import { JobFrequencyEnum } from "../../Contracts/CreateJobAndSchedule/CreateJobAndSchedule.model";
import { buildLeadFormDefaultValues, leadFormSchema } from "./LeadForm.utils";
import { useCityOptions } from "./useCityOptions";

export const useLeadForm = (leadId?: string) => {
  const { data: contractTypeOptionsData } = useQuery({
    queryKey: ["contract-type-options"],
    queryFn: () => {
      return requests.get("/contract-type?rows=1000");
    },
  });

  const { data: floorSurfacesOptionsData } = useQuery({
    queryKey: ["floor-surfaces-options"],
    queryFn: () => {
      return requests.get("/type-of-floor-surfaces");
    },
  });

  const { data: suppliesOptionsData } = useQuery({
    queryKey: ["supplies-options"],
    queryFn: () => {
      return requests.get("/supplies");
    },
  });

  const { data: paperProductOptionsData } = useQuery({
    queryKey: ["paper-product-options"],
    queryFn: () => {
      return requests.get("/paper-products");
    },
  });

  const projectTypeOptions = useMemo(() => {
    if (!contractTypeOptionsData) return [];

    return contractTypeOptionsData.result.map((option: any) => ({
      label: option.name,
      value: option.id,
      id: option.id,
    }));
  }, [contractTypeOptionsData]);

  const suppliesOptions = useMemo(() => {
    if (!suppliesOptionsData) return [];

    return suppliesOptionsData.result.map((option: any) => ({
      label: option.name,
      value: option.id,
    }));
  }, [suppliesOptionsData]);

  const paperProductOptions = useMemo(() => {
    if (!paperProductOptionsData) return [];

    return paperProductOptionsData.result.map((option: any) => ({
      label: option.name,
      value: option.id,
    }));
  }, [paperProductOptionsData]);

  const floorSurfaceOptions = useMemo(() => {
    if (!floorSurfacesOptionsData) return [];

    return floorSurfacesOptionsData.result.map((option: any) => ({
      label: option.name,
      value: option.id,
    }));
  }, [floorSurfacesOptionsData]);

  const hookForm = useForm<TLeadFormData>({
    resolver: yupResolver(leadFormSchema),

    // Jump to the first invalid field rather than failing silently.
    shouldFocusError: true,
    defaultValues: buildLeadFormDefaultValues(),
  });

  const { reset, watch } = hookForm;

  const [state, billingState, contractTypeId] = watch([
    "state",
    "billingState",
    "contractTypeId",
  ]);

  const { data: scopeOfWorkOptionsData } = useQuery({
    queryKey: ["scope-of-work-options", contractTypeId],
    enabled: !!contractTypeId,
    queryFn: () => {
      return requests.get(
        `/scope-of-work?rows=1000&contractTypeId=${contractTypeId}`
      );
    },
  });

  const scopeOfWorkOptions = useMemo(() => {
    if (!scopeOfWorkOptionsData) return [];

    return scopeOfWorkOptionsData.result.map((option: any) => ({
      label: option.name,
      value: option.id,
    }));
  }, [scopeOfWorkOptionsData]);

  const existingClientLookup = useMutation({
    mutationFn: (email: string) =>
      requests.get(`/client/by-email/${encodeURIComponent(email)}`),
  });

  const applyExistingClientToLead = (client: any) => {
    if (!client) return;

    reset((values) => ({
      ...values,
      customerCategory: client.customerCategory || values.customerCategory,
      name: client.name || values.name,
      surname: client.surname || values.surname,
      companyName: client.companyName || values.companyName,
      contactName: client.contactName || values.contactName,
      phone: client.phone || values.phone,
      mobile: client.mobile || values.mobile,
      fax: client.fax || values.fax,
      website: client.website || values.website,
      billingContact: client.contactName || client.name || values.billingContact,
      billingAddress: client.address || values.billingAddress,
      billingState: client.state || values.billingState,
      billingCity: client.city || values.billingCity,
      billingZIP: client.ZIP || values.billingZIP,
      billingStreet: client.street || values.billingStreet || "",
    }));
  };

  const cities = useCityOptions(state);
  const billingCities = useCityOptions(billingState);

  const currentLead = useQuery({
    queryKey: [`lead-form-${leadId}`],
    queryFn: () => {
      return requests.get(`/lead/${leadId}`);
    },
    enabled: false,
    cacheTime: 0,
    retry: false,
    refetchOnMount: false,
    retryOnMount: false,
    onSuccess: (data) => {
      const {
        billingZIP,
        billingCity,
        billingState,
        title,
        companyName,
        contactName,
        laborGoalBudget,
        laborGoalHours,
        pets,
        dayOfMonth,
        monthOfYear,
        frequency,
        recurEvery,
        daysOfWeek,
        recurrence,
        typeOfSpace,
        estimatedSQFT,
        depositAmount,

        phone,
        street,
        billingStreet,
        specialInstruction,
        email,
        billingAddress,
        ZIP,
        address,
        city,
        country,
        billingContact,
        customerCategory,
        surname,
        name,
        contractTypeId,
        projectTypeId,
        supplies,
        paperProducts,
        typeOfFloorSurfaces,
        createdDate,
        estimatedStartDate,
        scopeOfWorks,
        fax,
        website,
        mobile,
        state,
        status,
      } = data;

      reset({
        contractTypeId: contractTypeId || projectTypeId,
        projectTypeId,
        customerCategory,
        name,
        surname,
        billingContact,
        country,
        city,
        companyName,
        contactName,
        address,
        ZIP,
        billingAddress,
        email,
        phone,
        street: street || "",
        billingStreet: billingStreet || "",
        specialInstruction,
        estimatedSQFT,
        title,
        depositAmount,
        billingZIP,
        billingCity,
        billingState,
        dayOfMonth: dayOfMonth || 1,
        monthOfYear: monthOfYear || 0,
        frequency: frequency || JobFrequencyEnum.WEEKLY,
        recurEvery: recurEvery || 1,
        daysOfWeek: [],
        fax,
        mobile,
        website,
        state,
        laborGoalHours,
        laborGoalBudget,
        createdDate: new Date(createdDate),
        estimatedStartDate: new Date(estimatedStartDate),
      });
    },
  });

  useEffect(() => {
    if (leadId && !contractTypeId) {
      currentLead.refetch();
    }
  }, [leadId, currentLead, contractTypeId]);

  return {
    hookForm,
    projectTypeOptions,
    filteredScopeOfWorkOptions: scopeOfWorkOptions,
    suppliesOptions,
    paperProductOptions,
    floorSurfaceOptions,
    cities,
    billingCities,
    currentLead,
    existingClientLookup,
    applyExistingClientToLead,
  };
};
